import { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'

import { Button, useShowMessage } from '@pepper/ui'
import { pluralize } from '@pepper/utils/src/function/pluralize'

import { createPrivateDocument } from 'utils/api/item'
import { ModalBody, ModalFooter } from 'components/ModalWrapper'

import styles from './SuccessUI.module.css'

type SuccessUIProps = {
  title: string
  topicsCount: number
  itemsCount: number
  projectId: string
  draftId: string
  closeModal: () => void
  modalType: 'docs' | 'project'
  companyId: string
  topics: string[]
  shouldCreatePrivateDoc: boolean
}

export const SuccessUI = ({
  title,
  topicsCount,
  itemsCount,
  closeModal,
  projectId,
  draftId,
  modalType,
  companyId,
  topics,
  shouldCreatePrivateDoc,
}: SuccessUIProps) => {
  const history = useHistory()
  const showMessage = useShowMessage()

  const [isDocumentCreating, setIsDocumentCreating] = useState(true)
  const [isDocumentCreationFailed, setIsDocumentCreationFailed] =
    useState(false)
  const [data, setData] = useState<{ item_id: string }[]>([])

  useEffect(() => {
    async function fetchData() {
      const results = []
      for (const topicName of topics) {
        const responseData = await createPrivateDocument({
          projectId,
          title: topicName,
        })
        if (responseData?.data.status === 'success') {
          results.push(responseData.data)
        } else {
          showMessage({
            type: 'error',
            message: `Unable to create document for ${topicName}. Please try again`,
          })
          setIsDocumentCreationFailed(true)
        }
      }
      setIsDocumentCreating(false)
      setData(results)
    }
    if (shouldCreatePrivateDoc) {
      fetchData()
    }
  }, [])

  return (
    <>
      <ModalBody>
        <div className={styles.draftCreatedContainer}>
          <span className={styles.draftMessage}>
            {modalType === 'docs'
              ? `A Pepper Doc has been created for the selected topic(s).`
              : `${
                  topicsCount > 0 ? 'All the topics' : 'The topic'
                } selected have been added as Item drafts. ${
                  topicsCount > 0 ? 'These items' : 'This item'
                }
            will be assigned to creators once you complete the brief.`}
          </span>
          <div className={styles.draftInfoContainer}>
            <div className={styles.draftInfo}>
              <span className={styles.projectName}>{title}</span>
              <span className={styles.draftItemsInfo}>
                {modalType === 'docs'
                  ? `+ ${pluralize(topicsCount, 'new doc')} created`
                  : ` + ${pluralize(topicsCount, 'item')} saved as draft`}
              </span>
              {modalType !== 'docs' && (
                <span className={styles.totalDraftsInfo}>
                  You now have {pluralize(itemsCount, 'item')} saved as draft in
                  this project.
                </span>
              )}
            </div>
            <div className={styles.draftActionButtonContainer}>
              {modalType === 'project' && (
                <>
                  <Button
                    type="primary"
                    onClick={() => {
                      history.push(`/create-project-flow?draft_id=${draftId}`)
                      closeModal()
                    }}
                    className={styles.submitButton}
                    data-id="complete-brief-btn"
                  >
                    Complete The Brief
                  </Button>
                  <Button
                    type="link"
                    onClick={() => {
                      history.push(
                        `/project/${projectId}/items?item_types=assignment`
                      )
                      closeModal()
                    }}
                  >
                    Go To Project
                  </Button>
                </>
              )}
              {modalType === 'docs' && topicsCount > 1 && (
                <Button
                  type="primary"
                  onClick={() => {
                    closeModal()
                    history.push(
                      `/project/${projectId}/items?item_types=private_doc`
                    )
                  }}
                  className={styles.submitButton}
                  loading={isDocumentCreating}
                >
                  Go To Project
                </Button>
              )}
              {modalType === 'docs' && topicsCount === 1 && (
                <>
                  <Button
                    type="primary"
                    onClick={() => {
                      closeModal()
                      window.open(
                        `/${companyId}/items/${data[0]?.item_id}`,
                        '_blank'
                      )
                    }}
                    loading={isDocumentCreating}
                    disabled={isDocumentCreationFailed}
                    className={styles.submitButton}
                    data-id="open-docs-btn"
                  >
                    Go to Docs
                  </Button>
                  <Button
                    type="link"
                    onClick={() => {
                      history.push(
                        `/project/${projectId}/items/?item_types=private_doc`
                      )
                      closeModal()
                    }}
                  >
                    Go To Project
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          type="default"
          onClick={closeModal}
          className={styles.continueResearchButton}
        >
          Continue research
        </Button>
      </ModalFooter>
    </>
  )
}
